import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { dataVersionState } from '../../state.js';
import { api, ApiError } from '../../api.js';
import { Card } from '../Card.js';
import { SubmitButton } from '../FormBits.js';
import { IngestFailure } from './IngestFailure.js';
import { LayoutConfirm } from './LayoutConfirm.js';
import { LabelConfirm } from './LabelConfirm.js';
import { SummaryConfirm } from './SummaryConfirm.js';
import { UploadNotices } from './UploadMessageView.js';
import type { LabelMappings, LabelRecovery, SheetRecovery, SummaryAnswer, SummaryRecovery, UploadMessage, WorkbookLayout } from '@agio/core';

interface IngestResult {
  jobId: string;
  clientId: string;
  counts: Record<string, number>;
  issueCounts: { error: number; warn: number; info: number };
  /** Loaded, but something the admin should know about. */
  notices?: UploadMessage[];
  /** The duration and hedged figures read from the master for the policy control. */
  policy?: {
    status: 'read' | 'failed' | 'off';
    funds: { fund: string; duration: number | null; duration_where: string | null; hedged: number | null; hedged_where: string | null; problems: string[] }[];
    error?: string;
  };
}

type IngestResponse =
  | ({ status: 'loaded' } & IngestResult)
  | { status: 'needs_confirmation'; recoveries: SheetRecovery[]; labels: LabelRecovery[]; summary?: SummaryRecovery | null };

/** What the server stopped on, waiting for the admin's yes. */
interface Pending {
  recoveries: SheetRecovery[];
  labels: LabelRecovery[];
  summary: SummaryRecovery | null;
}

/** Admin: initiate/refresh an account from a master workbook. */
export function NewClientCard() {
  const bump = useSetRecoilState(dataVersionState);
  const [clientId, setClientId] = useState('');
  const [displayNameHe, setDisplayNameHe] = useState('');
  const [workbook, setWorkbook] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);
  // Sheets the rules could not read, or headings without a name, waiting for
  // the admin's yes. The picked file stays: confirming re-sends it together
  // with what was confirmed. A layout or headings confirmed in an earlier
  // round are kept for the next one, so a file that needs several is not
  // asked twice.
  const [pending, setPending] = useState<Pending | null>(null);
  const [layout, setLayout] = useState<WorkbookLayout | null>(null);
  const [labels, setLabels] = useState<LabelMappings | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [formKey, setFormKey] = useState(0); // bump remounts the form → full reset
  // Bumped on failure to clear ONLY the file picker: the old File points at the
  // pre-fix version on disk, so re-submitting without re-picking would upload
  // the broken workbook again (or fail to read it at all).
  const [fileKey, setFileKey] = useState(0);

  const send = async (confirmedLayout: WorkbookLayout | null, confirmedLabels: LabelMappings | null, confirmedSummary: SummaryAnswer | null = null) => {
    if (!workbook) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.set('clientId', clientId);
      form.set('displayNameHe', displayNameHe);
      form.set('workbook', workbook);
      if (confirmedLayout) form.set('layout', JSON.stringify(confirmedLayout));
      if (confirmedLabels) form.set('labels', JSON.stringify(confirmedLabels));
      if (confirmedSummary) form.set('summary', JSON.stringify(confirmedSummary));
      const res = await api.upload<IngestResponse>('/admin/ingest/master', form);
      if (res.status === 'needs_confirmation') {
        setPending({ recoveries: res.recoveries, labels: res.labels ?? [], summary: res.summary ?? null });
        return;
      }
      setPending(null);
      setLayout(null);
      setLabels(null);
      setResult(res);
      bump((v) => v + 1);
      // reset the form for the next account
      setClientId('');
      setDisplayNameHe('');
      setWorkbook(null);
      setFormKey((k) => k + 1);
    } catch (err) {
      setError(err as Error);
      setPending(null);
      setLayout(null);
      setLabels(null);
      setWorkbook(null);
      setFileKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    void send(null, null);
  };

  const confirmLayout = (recoveries: SheetRecovery[]) => {
    const confirmed = Object.fromEntries(recoveries.map((r) => [r.sheet, r.layout]));
    setLayout(confirmed);
    void send(confirmed, null);
  };

  const cancelPending = () => {
    setPending(null);
    setLayout(null);
    setLabels(null);
    setWorkbook(null);
    setFileKey((k) => k + 1);
  };

  return (
    <Card title="העלאת קובץ מאסטר (Excel/CSV)">
      <p className="-mt-1 mb-4 max-w-2xl text-sm text-muted-foreground">
        גם לעדכון לקוח קיים: הזינו את המזהה שלו והעלו את קובץ המאסטר המעודכן. דוחות PDF חודשיים מעלים במסך "עדכון חודשי".
      </p>
      <form key={formKey} className="grid max-w-2xl gap-4" onSubmit={submit}>
        <fieldset disabled={busy} className="contents">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">מזהה לקוח (slug באנגלית)</span>
            <input dir="ltr" required pattern="[A-Za-z0-9][A-Za-z0-9_\-]*" placeholder="client-name"
              className="w-full rounded-md border border-input bg-popover px-3 py-2"
              value={clientId} onChange={(e) => setClientId(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">שם תצוגה בעברית</span>
            <input required placeholder="שם הלקוח"
              className="w-full rounded-md border border-input bg-popover px-3 py-2"
              value={displayNameHe} onChange={(e) => setDisplayNameHe(e.target.value)} />
          </label>
        </div>
        <label className="text-sm">
          <span className="mb-1 block text-muted-foreground">קובץ מאסטר של הלקוח (.xlsx / .xls / .csv)</span>
          <input key={fileKey} required type="file" accept=".xlsx,.xls,.xlsb,.csv"
            className="w-full rounded-md border border-input bg-popover px-3 py-2 file:me-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-foreground"
            onChange={(e) => setWorkbook(e.target.files?.[0] ?? null)} />
        </label>
        <SubmitButton busy={busy} busyLabel="קולט חשבון… (עשוי לקחת דקה)">קלוט חשבון</SubmitButton>
        </fieldset>
      </form>
      <IngestFailure error={error} />
      {pending && pending.recoveries.length > 0 && (
        <LayoutConfirm recoveries={pending.recoveries} busy={busy} onConfirm={() => confirmLayout(pending.recoveries)} onCancel={cancelPending} />
      )}
      {pending && pending.recoveries.length === 0 && pending.labels.length > 0 && (
        <LabelConfirm recoveries={pending.labels} busy={busy} onConfirm={(confirmed) => { setLabels(confirmed); void send(layout, confirmed); }} onCancel={cancelPending} />
      )}
      {pending && pending.recoveries.length === 0 && pending.labels.length === 0 && pending.summary && (
        <SummaryConfirm key={JSON.stringify(pending.summary)} recovery={pending.summary} busy={busy} onConfirm={(answer) => void send(layout, labels, answer)} onCancel={cancelPending} />
      )}
      {result && (
        <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 p-4">
          <p className="mb-3 font-semibold text-emerald-800">✓ החשבון נקלט בהצלחה ({result.clientId})</p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {Object.entries(result.counts).map(([table, n]) => (
              <div key={table} className="rounded border border-emerald-200 bg-white px-3 py-2">
                <div className="text-xs text-muted-foreground" dir="ltr">{table}</div>
                <div className="font-bold tabular-nums">{n}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm">
            בעיות איכות נתונים:{' '}
            <span className={result.issueCounts.error ? 'font-semibold text-destructive' : ''}>{result.issueCounts.error} שגיאות</span>
            {' · '}<span>{result.issueCounts.warn} אזהרות</span>
            {' · '}<span className="text-muted-foreground">{result.issueCounts.info} מידע</span>
            {' — '}<a className="underline" href="/admin/clients">בקרת נתונים</a>
          </p>
          <UploadNotices notices={result.notices ?? []} />
          {result.policy && result.policy.status !== 'off' && <PolicyCellsRead policy={result.policy} />}
        </div>
      )}
    </Card>
  );
}

const ils = (n: number) => `₪${Math.round(n).toLocaleString('en-US')}`;

/** What the master said for the policy control, fund by fund, and where it was read. */
function PolicyCellsRead({ policy }: { policy: NonNullable<IngestResult['policy']> }) {
  if (policy.status === 'failed') {
    return <p className="mt-3 text-sm text-amber-800">בקרת מדיניות: {policy.error}</p>;
  }
  if (policy.funds.length === 0) return null;
  return (
    <div className="mt-3 rounded-md border border-emerald-200 bg-white p-3 text-sm">
      <p className="font-semibold">לבקרת המדיניות נקראו מהמאסטר</p>
      <ul className="mt-2 space-y-2">
        {policy.funds.map((f) => (
          <li key={f.fund}>
            <span className="font-medium">{f.fund}: </span>
            <span>{f.duration !== null ? `מח"מ ${f.duration}` : 'מח"מ לא נמצא'}</span>
            {' · '}
            <span>{f.hedged !== null ? `מנוטרל ${ils(f.hedged)}` : 'מנוטרל לא נמצא'}</span>
            {(f.duration_where || f.hedged_where) && (
              <div className="text-xs text-muted-foreground">{[f.duration_where, f.hedged_where].filter(Boolean).join(' · ')}</div>
            )}
            {f.problems.map((p) => <div key={p} className="text-xs text-amber-800">{p}</div>)}
          </li>
        ))}
      </ul>
    </div>
  );
}
